/**
 * Every line the account control says to a player (D157 addendum) — in one
 * place, named by the state it answers, so the words are the design lane's
 * to re-cut without touching the wiring (Calder's §4: this is the first
 * time the product speaks to a player outside the fiction, and it speaks
 * in the platform's voice, not EURYDICE's).
 *
 * The dialog's standing labels and leads live with the dialog in
 * `player-ui`; these are the shell's notices — what a door said after it
 * was tried — and the words for arriving back from a door that did not
 * open (a magic link, or a provider that refused; both return with
 * `?error=`).
 *
 * **The register, ruled 2026-09-08 (Calder).** Plain, second person, no
 * flourish — the chrome's voice, the one `WAKE TERMINAL` and `Export` are
 * written in, and never EURYDICE's. An account spans every game on the
 * platform and holds a real address: putting a fictional facility in
 * charge of a real credential is a category error, and it is also exactly
 * the costume a phishing page wears. The game's voice is for the game.
 *
 * **What a state is NOT told**, and it is deliberate: a session revoked or
 * expired somewhere else announces nothing. The trigger simply reads "Sign
 * in" again. The shell narrates what happened HERE; a line about an
 * elsewhere the player cannot see is either noise (an ordinary expiry) or
 * redundant (they revoked it themselves, from the device they revoked it
 * on). Silence is the same rule as the empty search line: say the true
 * thing, and when there is nothing true to add, add nothing.
 *
 * @packageDocumentation
 */

import { MAGIC_LINK_LIFETIME_MINUTES } from '@orpheus/api-contracts';

/** What the shell says after a door. */
export const ACCOUNT_NOTICE = {
	/** A magic link that landed back here without signing in — Better Auth's `?error=` code, when it is one we can name. */
	linkFailed: {
		/** Anything else that lands back here with `?error=` — a provider that refused, a storage fault, a refused sign-up. */
		fallback: 'That sign-in did not go through. Try again, or another way in.',
		/**
		 * Used already, expired, or never a link at all — the library does not
		 * distinguish, and neither should the player have to.
		 *
		 * The lifetime is a fact about the code, not a figure of speech: it is
		 * the contract's `MAGIC_LINK_LIFETIME_MINUTES`, the same number the
		 * Worker mints the link with and the email states — one fact, told
		 * in three places from one source, so it cannot drift.
		 */
		INVALID_TOKEN: `That link has been used, or it has expired. Send another — it works once, and only for ${String(MAGIC_LINK_LIFETIME_MINUTES)} minutes.`,
	},
	/**
	 * The link signs in whatever OPENS it, which is the point of a link and
	 * the thing a player gets wrong: they ask from a laptop, tap it on a
	 * phone, and wonder why the laptop is still signed out. Said here and in
	 * the email, because they read whichever reaches them first.
	 */
	linkSent: 'Check your email. The link works once, and signs in whichever device opens it.',
	passkeyAdded: 'Passkey added. It signs you in on this device from now on.',
	/**
	 * The doors' refusals when the library gives no reason of its own. Each
	 * says the one true thing and stops — no apology, no advice the player
	 * did not ask for, and never a guess at a cause we do not have.
	 */
	refused: {
		/** D170: the token the page arrived with did not exchange — used, expired, or not one. */
		acceptHandoff: 'That sign-in did not carry over. Try Sign in again.',
		addPasskey: 'The passkey was not added.',
		sendMagicLink: 'The link was not sent.',
		// Canceled at the browser prompt, or refused by the authenticator. The
		// old line ("No passkey signed in") read as though a passkey had tried
		// and failed; what is true of both causes is that nothing happened.
		signInWithPasskey: 'That did not sign you in.',
		signInWithSocial: 'The sign-in did not start.',
		signOut: 'Still signed in.',
		/** D173: the limiter — a few tries in a few seconds. The library's own line ("Too many requests. Please try again later.") is not the register. PLACEHOLDER; Calder's. */
		/*
		 * D173: the limiter. "Too many tries" put the player in the subject
		 * of a sentence about a fault — and in the case that produced this
		 * line, the retries were OURS: the dialog offered Google and Apple,
		 * neither was configured, and each refusal invited another press.
		 * Blaming someone for exhausting a limit we walked them into is the
		 * wrong sentence even now that §1 has stopped it happening. The
		 * attempts are the subject; the limit is the thing refusing (§2.6);
		 * and "in a row" says the cause, which is what makes waiting an
		 * obvious fix rather than a shrug.
		 */
		tooMany: 'Too many sign-in attempts in a row — try again in a moment.',
		/**
		 * D172: no door answered at all — the identity Worker could not be
		 * reached (offline, a hostname that does not resolve, a blocked
		 * request). Said in place of the browser's "Failed to fetch", which
		 * is true and not the register. PLACEHOLDER; Calder's to re-cut.
		 */
		unreachable: 'The sign-in service could not be reached. Try again in a moment.',
	},
} as const;

/**
 * D170: the handoff's lines — a creator's own domain cannot see the
 * account's cookie, so signing in there is a trip to the home site and
 * back. PLACEHOLDERS, in the register; Calder's to re-cut.
 */
export const HANDOFF_NOTICE = {
	/** The one door's label. */
	awayLabel: 'Sign in on orpheusquest.com',
	/**
	 * On the creator's domain, signed out: the lead above the one door.
	 *
	 * @remarks
	 * **This line carries a security burden and not only an explanatory
	 * one.** "Sign in by going to a different domain" is the exact shape of
	 * a phishing redirect, and a player right to be suspicious of it has no
	 * way to tell ours from one — so the sentence has to make the trip make
	 * SENSE, not merely announce it. The reason it makes sense is the
	 * platform: one account spans every game on it (D157), which is why the
	 * account is not here. Saying that converts an unexplained hop into an
	 * obvious consequence of something the player now understands.
	 */
	awayLead: 'One account covers every game on Orpheus, and it lives on orpheusquest.com. Sign in there and you come straight back here, signed in.',
	/**
	 * On the home host, mid-handoff: where they came from and what to do.
	 *
	 * @remarks
	 * An imperative rather than a progressive, because the player usually
	 * has to ACT. "Signing you in for {host}" describes something happening
	 * by itself, which is true only for the case where they were already
	 * signed in — and that case flashes past unread. The case anybody
	 * actually reads this in is the one where the account dialog is sitting
	 * open below it waiting for them, and a line claiming the work is
	 * already under way is wrong exactly then.
	 */
	homeLead: (returnHostname: string) => `You came from ${returnHostname}. Sign in here and you go straight back.`,
	/** On the home host: signed in, but the mint failed — nothing to return with. */
	notMinted: 'The sign-in could not be carried over. Try again from the game.',
	/** On the home host: the return address is not a page we serve — no token leaves for it. */
	notServed: (returnHostname: string) => `${returnHostname} is not a site this platform serves, so nothing was sent there.`,
	/**
	 * On the home host: the heading.
	 *
	 * @remarks
	 * **Read on the deployed page (D174), and it had the defect I had just
	 * fixed one line below it.** "Signing in" is the same false progressive
	 * `homeLead` was: it describes work under way, and the page it heads has
	 * an account dialog sitting open waiting for the player to act. I kept
	 * this line when I re-cut the other six because I reviewed them as a
	 * list; on screen, stacked, the two disagreed in a way no reading of
	 * either alone could show.
	 *
	 * It stays a PURPOSE heading rather than becoming the brand, even though
	 * the page could badly use a wordmark (it has no mark at all, which is
	 * its own finding and not a string's to fix): this is the page's
	 * landmark heading, so what a screen reader announces on arrival should
	 * be what the page is FOR.
	 */
	title: 'Sign in to continue',
} as const;

/** The `?error=` a magic link redirects back with, when the URL carries one. */
export const linkFailureNoticeOf = (searchString: string): string | undefined => {
	const code = new URLSearchParams(searchString).get('error');

	if (code === null) {
		return undefined;
	}

	return code === 'INVALID_TOKEN' ? ACCOUNT_NOTICE.linkFailed.INVALID_TOKEN : ACCOUNT_NOTICE.linkFailed.fallback;
};
